/**
 * Localized (multi-currency) Stripe price display for the web paywall.
 *
 * Background. Stripe charges from a price's `currency_options`, but the
 * Firestore mirror the web reads (written by the Stripe Firebase extension at
 * products/{productId}/prices/{priceId}) carries only the base `unit_amount`
 * and `currency`. Until 2026-09 every web brand shipped a hand-typed
 * `prices.js` table of localized amounts, which drifted from Stripe (JPY 100x
 * too high, a ¥159,999 refund; seven currencies at 20-35% of USD) and coupled
 * every price change to a web deploy.
 *
 * Now the localized amounts ride on the Stripe price itself, in
 * `metadata.localized_amounts` (a compact JSON map of currency -> minor units,
 * derived from `currency_options` by `npm run prices:sync`). The extension
 * mirrors `metadata` into Firestore on every price update, so the web reads
 * them from the same document it already reads — no second doc, no paste, no
 * deploy. This module is the pure logic on both sides:
 *
 *   localizedAmountsFromStripe  Stripe API price -> amounts map   (sync)
 *   serialize/parseLocalizedAmounts   <-> the metadata string      (sync, web)
 *   buildLocalizedCatalog / diffLocalizedCatalogs                  (check)
 *   resolveDisplayPrices        mirror rows + visitor currency -> what to show (web)
 *   stripePriceKey              the recurring_N / one_time_N plan key
 *
 * Invariants:
 *   - Metadata only ever says what `currency_options` says. Never hand-edit it.
 *   - Display is all-or-nothing per product: every active price localizes for
 *     the visitor's currency or every price shows its base (USD) amount. Mixed
 *     currencies on one grid would make the "Save X%" badges lie.
 *   - A price whose metadata is missing, unparsable, or disagrees with the live
 *     base amount falls back to base — and Stripe Checkout charges the base
 *     amount too when no currency option applies. Fallback can be unlocalized,
 *     never different from the charge.
 *   - Currency codes uppercase (the web's currencyUtils vocabulary); amounts in
 *     Stripe minor units (cents, paise, yen).
 */

/** Stripe price metadata key that carries the JSON amounts map. */
export const LOCALIZED_AMOUNTS_METADATA_KEY = 'localized_amounts';
/** Stripe's per-value metadata limit. 18 currencies serialize to ~250 chars. */
export const STRIPE_METADATA_VALUE_MAX = 500;

const upper = (code) => String(code ?? '').toUpperCase();
const isAmount = (n) => typeof n === 'number' && Number.isFinite(n) && n >= 0;
const sortKeys = (obj) => Object.fromEntries(Object.keys(obj).sort().map((k) => [k, obj[k]]));

/**
 * The paywall plan key for a Stripe price, in either shape the app meets:
 * the Firestore mirror (flat `interval`/`interval_count`, nickname mirrored as
 * `description`) or the Stripe API object (`recurring.{interval,interval_count}`,
 * `nickname`). One-time passes are keyed by their nickname ("1", "3", "6", "12").
 *
 * @returns {'recurring_1'|'recurring_3'|'recurring_6'|'recurring_12'|
 *           'one_time_1'|'one_time_3'|'one_time_6'|'one_time_12'|null}
 */
export const stripePriceKey = (price) => {
  if (!price || typeof price !== 'object') return null;
  if (price.type === 'recurring') {
    const interval = price.interval ?? price.recurring?.interval;
    const count = price.interval_count ?? price.recurring?.interval_count;
    if (interval === 'month' && [1, 3, 6].includes(count)) return `recurring_${count}`;
    if (interval === 'year' && count === 1) return 'recurring_12';
    return null;
  }
  if (price.type === 'one_time') {
    const nick = price.description ?? price.nickname;
    if (nick !== null && nick !== undefined && ['1', '3', '6', '12'].includes(String(nick))) {
      return `one_time_${nick}`;
    }
    return null;
  }
  return null;
};

/**
 * Amounts map for one Stripe API price (listed with `expand[]=data.currency_options`):
 * base currency + every currency option. null for an inactive price or one
 * without a plain unit amount (tiered / custom), which is never localized.
 *
 * @returns {Object<string, number>|null}
 */
export const localizedAmountsFromStripe = (price) => {
  if (!price || price.active !== true || !isAmount(price.unit_amount)) return null;
  const amounts = { [upper(price.currency)]: price.unit_amount };
  for (const [code, opt] of Object.entries(price.currency_options ?? {})) {
    if (isAmount(opt?.unit_amount)) amounts[upper(code)] = opt.unit_amount;
  }
  return sortKeys(amounts);
};

/** The metadata string for an amounts map. Deterministic (sorted keys); refuses Stripe's limit. */
export const serializeLocalizedAmounts = (amounts) => {
  const clean = {};
  for (const [code, n] of Object.entries(amounts ?? {})) if (isAmount(n)) clean[upper(code)] = n;
  const s = JSON.stringify(sortKeys(clean));
  if (s.length > STRIPE_METADATA_VALUE_MAX) {
    throw new Error(`localized_amounts is ${s.length} chars; Stripe metadata values max ${STRIPE_METADATA_VALUE_MAX}`);
  }
  return s;
};

/**
 * Tolerant inverse of serializeLocalizedAmounts. null for anything that is not
 * a non-empty object of finite non-negative numbers — a bad or hand-edited
 * value must degrade to "no localization", never throw on the paywall.
 *
 * @returns {Object<string, number>|null}
 */
export const parseLocalizedAmounts = (value) => {
  if (typeof value !== 'string' || !value) return null;
  let parsed;
  try { parsed = JSON.parse(value); } catch (e) { return null; }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  const out = {};
  for (const [code, n] of Object.entries(parsed)) if (isAmount(n)) out[upper(code)] = n;
  return Object.keys(out).length ? sortKeys(out) : null;
};

/**
 * { [priceId]: amounts } for the ACTIVE prices of a product, from either side:
 *   'currency_options' — what Stripe will charge (the source of truth)
 *   'metadata'         — what the price's metadata currently says (what the web shows)
 * Prices with nothing on the chosen side are omitted, so a diff reports them.
 */
export const buildLocalizedCatalog = (stripePrices, from = 'currency_options') => {
  if (!['currency_options', 'metadata'].includes(from)) throw new Error(`buildLocalizedCatalog: unknown side ${from}`);
  const catalog = {};
  for (const price of Array.isArray(stripePrices) ? stripePrices : []) {
    if (!price || price.active !== true || !price.id) continue;
    const amounts = from === 'metadata'
      ? parseLocalizedAmounts(price.metadata?.[LOCALIZED_AMOUNTS_METADATA_KEY])
      : localizedAmountsFromStripe(price);
    if (amounts) catalog[price.id] = amounts;
  }
  return sortKeys(catalog);
};

/**
 * Differences between two catalogs (expected = currency_options, actual =
 * metadata or the Firestore mirror). Empty array = the web shows exactly what
 * Stripe charges.
 *
 * @returns {Array<{kind: string, priceId: string, currency?: string, expected?: number, actual?: number}>}
 */
export const diffLocalizedCatalogs = (expected, actual) => {
  const a = expected ?? {};
  const b = actual ?? {};
  const out = [];
  for (const priceId of Object.keys(a).sort()) {
    if (!b[priceId]) { out.push({ kind: 'missing_price', priceId }); continue; }
    for (const currency of Object.keys(a[priceId]).sort()) {
      if (!(currency in b[priceId])) out.push({ kind: 'missing_currency', priceId, currency, expected: a[priceId][currency] });
      else if (a[priceId][currency] !== b[priceId][currency]) {
        out.push({ kind: 'amount', priceId, currency, expected: a[priceId][currency], actual: b[priceId][currency] });
      }
    }
    for (const currency of Object.keys(b[priceId]).sort()) {
      if (!(currency in a[priceId])) out.push({ kind: 'extra_currency', priceId, currency, actual: b[priceId][currency] });
    }
  }
  for (const priceId of Object.keys(b).sort()) {
    if (!a[priceId]) out.push({ kind: 'extra_price', priceId });
  }
  return out;
};

/**
 * One mirrored price for one visitor currency.
 *
 * @returns {{unitAmount: number|null, currencyCode: string, localized: boolean, stale: boolean}}
 *   localized=false: base currency shown (no/invalid metadata, currency not
 *   offered, or the visitor is already in the base currency). stale=true: the
 *   metadata's base amount disagrees with the live mirror, so it was distrusted.
 */
export const resolveDisplayPrice = (mirrorPrice, currencyCode) => {
  const baseCode = upper(mirrorPrice?.currency || 'usd');
  const base = {
    unitAmount: isAmount(mirrorPrice?.unit_amount) ? mirrorPrice.unit_amount : null,
    currencyCode: baseCode,
    localized: false,
    stale: false,
  };
  const amounts = parseLocalizedAmounts(mirrorPrice?.metadata?.[LOCALIZED_AMOUNTS_METADATA_KEY]);
  if (!amounts) return base;
  if (amounts[baseCode] !== base.unitAmount) return { ...base, stale: true };
  const code = upper(currencyCode);
  if (!code || code === baseCode || !isAmount(amounts[code])) return base;
  return { unitAmount: amounts[code], currencyCode: code, localized: true, stale: false };
};

/**
 * All active prices of a product for one visitor — ALL-OR-NOTHING.
 *
 * Rows without a plain unit amount are dropped (never a "$0" tier). If every
 * remaining row localizes to the visitor's currency the grid is localized;
 * otherwise every row shows its base amount, so term discounts on the grid are
 * always computed within one currency. `stalePriceIds` / `missingPriceIds` say
 * why localization was withheld (for a console warning or an analytics event).
 *
 * @param {Array<object>} mirrorPrices Firestore mirror rows (+ `priceId`)
 * @param {string} currencyCode visitor currency
 * @returns {{localized: boolean, currencyCode: string, stalePriceIds: string[], missingPriceIds: string[],
 *            rows: Array<{price: object, unitAmount: number, currencyCode: string}>}}
 */
export const resolveDisplayPrices = (mirrorPrices, currencyCode) => {
  const list = (Array.isArray(mirrorPrices) ? mirrorPrices : [])
    .filter((p) => p && p.active === true && isAmount(p.unit_amount));
  const code = upper(currencyCode);
  const idOf = (p) => p.priceId ?? p.id ?? null;
  const resolved = list.map((price) => ({ price, display: resolveDisplayPrice(price, code) }));
  const wantsLocal = !!code && resolved.some((r) => code !== upper(r.price.currency || 'usd'));
  const localized = wantsLocal && resolved.every((r) => r.display.localized);
  const stalePriceIds = resolved.filter((r) => r.display.stale).map((r) => idOf(r.price));
  const missingPriceIds = wantsLocal && !localized
    ? resolved.filter((r) => !r.display.localized && !r.display.stale).map((r) => idOf(r.price))
    : [];
  const rows = resolved.map(({ price, display }) => (localized
    ? { price, unitAmount: display.unitAmount, currencyCode: display.currencyCode }
    : { price, unitAmount: price.unit_amount, currencyCode: upper(price.currency || 'usd') }));
  return {
    localized,
    currencyCode: localized ? code : (rows[0]?.currencyCode ?? 'USD'),
    stalePriceIds,
    missingPriceIds,
    rows,
  };
};
